import React, { useEffect, useState } from "react";
import { api } from "../utils/api";

const TABS = ["actifs", "archives", "jamais_utilises"];
const TAB_LABELS = { actifs: "✅ Actifs", archives: "📦 Archives", jamais_utilises: "⚠️ Inactifs" };

export default function AdminPage() {
  const [sections, setSections] = useState([]);
  const [tab, setTab] = useState("actifs");
  const [toast, setToast] = useState({ msg: "", type: "ok" });
  const [newCp, setNewCp] = useState({});
  const [editCp, setEditCp] = useState(null);
  const [editSection, setEditSection] = useState(null);
  const [newSection, setNewSection] = useState({ code: "", title: "", max_score: "" });
  const [addingSection, setAddingSection] = useState(false);
  const [confirm, setConfirm] = useState(null); // { type, id, label }

  useEffect(() => { reload(); }, []);

  async function reload() {
    const all = await api.getAllSections();
    setSections(all);
  }

  function showToast(msg, type = "ok") {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "ok" }), 3000);
  }

  // ── Filter sections by tab ─────────────────────────────────
  function getSectionsByTab() {
    if (tab === "actifs") {
      return sections.filter(s => s.is_active && !s.is_archived);
    }
    if (tab === "archives") {
      return sections.filter(s => s.is_archived);
    }
    if (tab === "jamais_utilises") {
      // Sections with no active checkpoints having scores → deletable
      return sections.filter(s => !s.is_archived && !s.is_active);
    }
    return sections;
  }

  // ── Section actions ────────────────────────────────────────
  async function toggleSection(sec) {
    await api.toggleSection(sec.id);
    showToast(sec.is_active ? "Section désactivée" : "Section réactivée");
    reload();
  }

  async function confirmDelete(type, id, label) {
    setConfirm({ type, id, label });
  }

  async function doDelete() {
    try {
      if (confirm.type === "section") {
        await api.deleteSection(confirm.id);
        showToast("✓ Section supprimée définitivement");
      } else {
        await api.deleteCheckpoint(confirm.id);
        showToast("✓ Point supprimé définitivement");
      }
      reload();
    } catch (e) {
      showToast("❌ " + e.message, "error");
    }
    setConfirm(null);
  }

  async function saveSection() {
    if (!newSection.code || !newSection.title || !newSection.max_score) {
      showToast("❌ Remplissez tous les champs", "error"); return;
    }
    await api.createSection({
      ...newSection,
      max_score: parseInt(newSection.max_score),
      order_index: sections.filter(s => !s.is_archived).length + 1
    });
    setNewSection({ code: "", title: "", max_score: "" });
    setAddingSection(false);
    showToast("Section ajoutee");
    reload();
  }

  async function saveEditSection() {
    try {
      const result = await api.updateSection(editSection.id, {
        title: editSection.title,
        max_score: parseInt(editSection.max_score),
        code: editSection.code,
      });
      if (result.version > editSection.version) {
        showToast("Nouvelle version creee - historique preserve", "info");
        setTab("archives");
      } else {
        showToast("Section modifiee");
      }
      setEditSection(null);
      reload();
    } catch (e) {
      showToast("❌ " + e.message, "error");
    }
  }

  // ── Checkpoint actions ─────────────────────────────────────
  async function addCheckpoint(sectionId) {
    const text = (newCp[sectionId] || "").trim();
    if (!text) return;
    await api.createCheckpoint({ section_id: sectionId, description: text, order_index: 99 });
    setNewCp(p => ({ ...p, [sectionId]: "" }));
    showToast("Point de controle ajoute");
    reload();
  }

  async function saveEditCp() {
    if (!editCp?.text?.trim()) return;
    try {
      const result = await api.updateCheckpoint(editCp.id, { description: editCp.text });
      if (result.version > editCp.version) {
        showToast("Nouvelle version creee - historique preserve", "info");
        setTab("archives");
      } else {
        showToast("Modifie");
      }
      setEditCp(null);
      reload();
    } catch (e) {
      showToast("❌ " + e.message, "error");
    }
  }

  async function toggleCp(cp) {
    await api.toggleCheckpoint(cp.id);
    showToast(cp.is_active ? "Point désactivé" : "Point réactivé");
    reload();
  }

  const displaySections = getSectionsByTab();

  return (
    <div>
      <div className="page-header-row page-header">
        <div>
          <h1>Paramétrage</h1>
          <p>Gérez les sections et points de contrôle · Historique des données préservé</p>
        </div>
        {tab === "actifs" && (
          <button className="btn btn-primary" onClick={() => setAddingSection(!addingSection)}>
            + Nouvelle section
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2" style={{ marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t} className={`btn btn-sm ${tab === t ? "btn-primary" : "btn-outline"}`}
            onClick={() => setTab(t)}>
            {TAB_LABELS[t]}
            <span style={{ marginLeft: 6, background: "rgba(255,255,255,.2)", borderRadius: 10, padding: "1px 7px", fontSize: 11 }}>
              {t === "actifs" && sections.filter(s => s.is_active && !s.is_archived).length}
              {t === "archives" && sections.filter(s => s.is_archived).length}
              {t === "jamais_utilises" && sections.filter(s => !s.is_archived && !s.is_active).length}
            </span>
          </button>
        ))}
      </div>

      {/* Tab descriptions */}
      {tab === "archives" && (
        <div style={{ background: "#fef3c7", border: "1px solid #d97706", borderRadius: 8, padding: "10px 16px", marginBottom: 16, fontSize: 13 }}>
          📦 Ces sections ont été remplacées par une nouvelle version. Leurs données historiques sont préservées dans les anciens rapports.
        </div>
      )}
      {tab === "jamais_utilises" && (
        <div style={{ background: "#fef3c7", border: "1px solid #d97706", borderRadius: 8, padding: "10px 16px", marginBottom: 16, fontSize: 13 }}>
          ⚠️ Sections desactivees. La suppression est <strong>bloquee automatiquement</strong> si des donnees de visites existent — le serveur refusera et affichera un message d erreur.
        </div>
      )}

      {/* New section form */}
      {addingSection && tab === "actifs" && (
        <div className="card" style={{ marginBottom: 20, borderLeft: "4px solid var(--accent)" }}>
          <div style={{ fontWeight: 700, marginBottom: 14, color: "var(--navy)" }}>Nouvelle section</div>
          <div className="form-grid-3">
            <div className="form-group">
              <label>Code (ex: I)</label>
              <input value={newSection.code} onChange={e => setNewSection(s => ({ ...s, code: e.target.value }))} maxLength={5} />
            </div>
            <div className="form-group">
              <label>Titre</label>
              <input value={newSection.title} onChange={e => setNewSection(s => ({ ...s, title: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Score max</label>
              <input type="number" value={newSection.max_score} onChange={e => setNewSection(s => ({ ...s, max_score: e.target.value }))} min="2" />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button className="btn btn-outline" onClick={() => setAddingSection(false)}>Annuler</button>
            <button className="btn btn-primary" onClick={saveSection}>Créer</button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {displaySections.length === 0 && (
        <div className="card empty-state">
          <div className="empty-state-icon">
            {tab === "actifs" ? "📋" : tab === "archives" ? "📦" : "🗑️"}
          </div>
          <h3>Aucune section {tab === "actifs" ? "active" : tab === "archives" ? "archivée" : "à supprimer"}</h3>
        </div>
      )}

      {/* Sections list */}
      {displaySections.map(sec => (
        <div key={sec.id} className="admin-section">
          <div className="admin-section-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
              {editSection?.id === sec.id ? (
                <div style={{ display: "flex", gap: 8, flex: 1, alignItems: "center" }}>
                  <input value={editSection.code} onChange={e => setEditSection(s => ({ ...s, code: e.target.value }))}
                    style={{ width: 60, fontSize: 12, padding: "4px 8px" }} />
                  <input value={editSection.title} onChange={e => setEditSection(s => ({ ...s, title: e.target.value }))}
                    style={{ flex: 1, fontSize: 12, padding: "4px 8px" }} />
                  <input type="number" value={editSection.max_score} onChange={e => setEditSection(s => ({ ...s, max_score: e.target.value }))}
                    style={{ width: 70, fontSize: 12, padding: "4px 8px" }} placeholder="Max pts" />
                  <button className="btn btn-sm" style={{ background: "var(--accent)", color: "var(--navy)" }} onClick={saveEditSection}>✓</button>
                  <button className="btn btn-sm" style={{ background: "transparent", border: "1px solid rgba(255,255,255,.3)", color: "white" }} onClick={() => setEditSection(null)}>✕</button>
                </div>
              ) : (
                <>
                  <span style={{ fontWeight: 700 }}>{sec.code}. {sec.title}</span>
                  <span style={{ fontSize: 11, opacity: .6 }}>max {sec.max_score} pts</span>
                  {sec.version > 1 && <span style={{ fontSize: 10, background: "rgba(232,184,75,.3)", color: "var(--accent)", padding: "2px 8px", borderRadius: 10 }}>v{sec.version}</span>}
                  {sec.is_archived && <span style={{ fontSize: 10, background: "rgba(255,100,0,.2)", color: "#fca5a5", padding: "2px 8px", borderRadius: 10 }}>Archivée</span>}
                  {!sec.is_active && !sec.is_archived && <span style={{ fontSize: 10, background: "rgba(255,0,0,.2)", color: "#fca5a5", padding: "2px 8px", borderRadius: 10 }}>Désactivée</span>}
                </>
              )}
            </div>
            {!editSection && (
              <div style={{ display: "flex", gap: 6 }}>
                {tab === "actifs" && (
                  <>
                    <button className="btn btn-sm" title="Modifier"
                      style={{ background: "rgba(255,255,255,.15)", color: "white", border: "none" }}
                      onClick={() => setEditSection({ id: sec.id, code: sec.code, title: sec.title, max_score: sec.max_score, version: sec.version })}>
                      ✏️
                    </button>
                    <button className="btn btn-sm"
                      style={{ background: "rgba(255,100,0,.25)", color: "#fca5a5", border: "none" }}
                      onClick={() => toggleSection(sec)}>
                      Désactiver
                    </button>
                  </>
                )}
                {tab === "jamais_utilises" && (
                  <>
                    <button className="btn btn-sm"
                      style={{ background: "rgba(22,163,74,.25)", color: "#86efac", border: "none" }}
                      onClick={() => toggleSection(sec)}>
                      Réactiver
                    </button>
                    <button className="btn btn-sm"
                      style={{ background: "rgba(220,38,38,.4)", color: "#fca5a5", border: "none" }}
                      onClick={() => confirmDelete("section", sec.id, sec.title)}>
                      🗑️ Supprimer
                    </button>
                  </>
                )}
                {tab === "archives" && (
                  <span style={{ fontSize: 11, opacity: .6 }}>Archivée — données préservées</span>
                )}
              </div>
            )}
          </div>

          {/* Checkpoints */}
          <div style={{ background: "white", borderRadius: "0 0 8px 8px", border: "1px solid var(--border)", borderTop: "none" }}>
            {sec.checkpoints
              .filter(cp => tab === "archives" ? true : tab === "jamais_utilises" ? !cp.is_active : cp.is_active && !cp.is_archived)
              .map((cp, i) => (
                <div key={cp.id} className="admin-row">
                  <span style={{ fontSize: 11, color: "var(--muted)", fontFamily: "var(--mono)", marginRight: 8, minWidth: 28 }}>
                    {sec.code}{i + 1}
                  </span>
                  {cp.version > 1 && (
                    <span style={{ fontSize: 10, background: "#fef3c7", color: "#d97706", padding: "1px 6px", borderRadius: 8, marginRight: 6 }}>v{cp.version}</span>
                  )}
                  {cp.is_archived && (
                    <span style={{ fontSize: 10, background: "#fee2e2", color: "#dc2626", padding: "1px 6px", borderRadius: 8, marginRight: 6 }}>archivé</span>
                  )}
                  {editCp?.id === cp.id ? (
                    <>
                      <input className="admin-cp-text" value={editCp.text}
                        onChange={e => setEditCp(p => ({ ...p, text: e.target.value }))}
                        style={{ fontSize: 13 }} />
                      <button className="btn btn-green btn-sm" onClick={saveEditCp}>✓</button>
                      <button className="btn btn-outline btn-sm" onClick={() => setEditCp(null)}>✕</button>
                    </>
                  ) : (
                    <>
                      <span className="admin-cp-text" style={{ color: cp.is_archived ? "var(--muted)" : "var(--text)" }}>
                        {cp.description}
                      </span>
                      {tab === "actifs" && (
                        <>
                          <button className="btn btn-outline btn-sm btn-icon"
                            onClick={() => setEditCp({ id: cp.id, text: cp.description, version: cp.version })} title="Modifier">✏️</button>
                          <button className="btn btn-danger btn-sm"
                            onClick={() => toggleCp(cp)} title="Désactiver">Désactiver</button>
                        </>
                      )}
                      {tab === "jamais_utilises" && (
                        <button className="btn btn-danger btn-sm btn-icon"
                          onClick={() => confirmDelete("checkpoint", cp.id, cp.description.substring(0, 40))} title="Supprimer">🗑️</button>
                      )}
                    </>
                  )}
                </div>
              ))}

            {/* Add checkpoint — only in actifs tab */}
            {tab === "actifs" && sec.is_active && (
              <div className="add-row">
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    placeholder="Ajouter un point de contrôle..."
                    value={newCp[sec.id] || ""}
                    onChange={e => setNewCp(p => ({ ...p, [sec.id]: e.target.value }))}
                    onKeyDown={e => e.key === "Enter" && addCheckpoint(sec.id)}
                    style={{ flex: 1, fontSize: 13 }}
                  />
                  <button className="btn btn-primary btn-sm" onClick={() => addCheckpoint(sec.id)}>+ Ajouter</button>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Confirm delete modal */}
      {confirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>⚠️ Supprimer définitivement ?</h2>
            <p style={{ marginBottom: 16, color: "var(--muted)", fontSize: 13 }}>
              Cette action est <strong>irréversible</strong>. Supprimer :
            </p>
            <div style={{ background: "#fee2e2", borderRadius: 8, padding: "12px 16px", marginBottom: 20, fontSize: 13, fontWeight: 600 }}>
              {confirm.label}
            </div>
            <div className="flex gap-2 justify-between">
              <button className="btn btn-outline" onClick={() => setConfirm(null)}>Annuler</button>
              <button className="btn btn-danger" onClick={doDelete}>🗑️ Supprimer définitivement</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast.msg && (
        <div className="toast" style={{ background: toast.type === "error" ? "var(--red)" : toast.type === "info" ? "var(--blue)" : "var(--navy)" }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
